/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2019, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import React, { Component } from 'react';
import PropTypes from 'util/PropTypes';
import Empty from 'components/empty/Empty';
import StringResources from 'nls/StringResources';

class PropertiesPanel extends Component {
	componentDidMount() {
		const { dashboardApi, widgetId } = this.props;
		this.content =  dashboardApi.getCanvas().getContent(widgetId);
		this.propertiesManager = dashboardApi.getFeature('propertiesManager');
		this._children = [];
		this._checkNewConditionalFormatting();
		this._renderProperties();
		this.props.dashboardApi.on('properties:refreshPane', this._onRefreshPropertiesPane, this);
	}

	// This event is triggered by live widget when there is a change in widget that needs to refresh properties pane.
	// If we are in child view, do not clear anything as we need to go back to previous view. 
	_onRefreshPropertiesPane() {
		const child = this.propertiesManager.getCurrentChild();
		if (!child) {
			// Clear all loaded properties to reload the updated one.
			this._clearExistingProperties();
			if (this.ref) {
				this.ref.innerHTML = '';
			}
			this._renderProperties();
		}
	}

	_checkNewConditionalFormatting() {
		const { dashboardApi } = this.props;
		const featureChecker = dashboardApi.getGlassCoreSvc('.FeatureChecker');
		const isNewCFForXtabTable = this._isNewCFForXtabTable(dashboardApi);
		const isNewCFForKPI = !(featureChecker.checkValue('dashboard', 'condFormat', 'disabled'));
		this._isNewCFEnabled = isNewCFForXtabTable || isNewCFForKPI;
	}

	_clearExistingProperties() {
		if (this._children && this._children.length) {
			const countViews = this._children.length;
			if (countViews === 1) {
				// only main properties, do not need to call PropertiesManager.closeChild
				this._children.pop();
			} else {
				const onCloseCb = this._removeChild.bind(this);
				for (let i = 1; i < this._children.length; ++i) {
					this.propertiesManager.closeChild(onCloseCb);
				}
				this._children.pop();
			}

		}
	}

	_removeChild(){
		this._children.pop();
	}

	componentWillUpdate(nextProps) {
		// change vis type case: content is the same but vis type will be changed, need to clear previous properties
		if (this.props.widgetType && this.props.widgetType !== nextProps.widgetType) {
			this._clearExistingProperties();
			if (this.ref) {
				this.ref.innerHTML = '';
			}
		}
	}

	componentWillUnmount() {
		this._clearExistingProperties();
		this.ref = null;
		this._children = null;
		this.content = null;
		this._isNewCFForXtabTableEnabled = null;
		if (this.condFormatting && this._isNewCFEnabled) {
			this.condFormatting.deregisterProvider('explore');
			this.condFormatting = null;
		}
		this._isNewCFEnabled = null;
		this.props.dashboardApi.off('properties:refreshPane', this._onRefreshPropertiesPane, this);
	}

	render() {
		const { dashboardApi, widgetType } = this.props;
		let isEmpty = false;

		if (this._isTableWidget(widgetType)) {
			isEmpty = !this._isNewCFForXtabTable(dashboardApi);
		}
		return isEmpty ? (
			<div className="card-layout-content__side-panel__empty-container">
				<Empty
					type={'default'}
					alignment={'left'}
					title={StringResources.get('properties_panel_no_props_title')}
					description={StringResources.get('properties_panel_no_props_description')}
				/>
			</div>
		): (
			<div className="explore-properties" ref={(node) => { this.ref = node; }} />
		);
	}

	_isVisSupportNewCF(visType) {
		return ['Crosstab', 'List', 'KPI'].indexOf(visType) > -1;
	}

	_renderProperties() {
		const { widgetId, isDisplayed, widgetType } = this.props;

		if (!isDisplayed) {
			return null;
		}

		if (!this.condFormatting && this._isNewCFEnabled && this._isVisSupportNewCF(widgetType)) {
			this.condFormatting = this.content.getFeature('ConditionalFormatting');
			this.condFormatting && this.condFormatting.registerProvider('explore', {
				addChildPropertyPanel: this.renderChildProperties.bind(this)
			});
		}

		const state = this.content.getFeature('state');
		state.whenStatusChanges(state.STATUS.RENDERED)
			.then(() => {
				this.propertiesManager.getPropertiesView(widgetId)
					.then(result => {
						if (result.el && this.ref) {
							const view = result.el;
							this._renderView(view);
						}
					});
			});
	}

	_renderView(view) {
		this._filterProperties(view);
		this._children.push(view);
		this._clearAndAppendChild(this.ref, view);
	}

	renderChildProperties(options) {
		if (this._isNewCFEnabled) {
			options.content.onCloseCb = this.onCloseChildCb.bind(this);
		}
		this.propertiesManager.getChildPropertiesView(options.content).then((result) => {
			const childView = result.$el && result.$el.get(0);
			if (childView && this.ref) {
				this._renderView(childView);
			}
		});
	}

	onCloseChildCb() {
		// clear child view and go back to parent panel view
		this._children.pop();
		if (this.ref && this._children.length) {
			const nextView = this._children[this._children.length -1];
			this._clearAndAppendChild(this.ref, nextView);
		}
	}

	_filterProperties(element) {
		// removes the banner, tabs and collapsible headers
		const tab = element.querySelector('ul.nav.nav-tabs');
		tab && tab.parentNode.removeChild(tab);

		const banner = element.querySelector('.propertyRow.banner.flex.property_banner');
		banner && banner.parentNode.removeChild(banner);
		const { dashboardApi, widgetType } = this.props;
		if (!(this._isCrosstabWidget(widgetType) && this._isNewCFForXtabTable(dashboardApi))) {
			// When new conditional-formatting is on, do not remove section title for crosstab (same as dashboard)
			const collapsible = element.querySelectorAll('.simpleCollapsibleSectionTitle.propertyRow');
			if (collapsible.length) {
				// IE 11 doesn't like for of
				for (let i = 0; i < collapsible.length; i++) {
					collapsible[i].parentNode.removeChild(collapsible[i]);
				}
			}
		}
		// make sure all collapsible properties are visible
		const properties = element.querySelectorAll('.collapsibleSection.property_chart.propertyUIControl');
		if (properties) {
			for (let i = 0; i < properties.length; i++) {
				// classList.add is not supported in IE 11
				properties[i].className += ' visible';
			}
		}
	}

	_clearAndAppendChild(node, child) {
		node.innerHTML = '';
		node.appendChild(child);
	}

	_isTableWidget (widgetType) {
		return widgetType == 'List';
	}

	_isCrosstabWidget (widgetType) {
		return widgetType == 'Crosstab';
	}

	_isNewCFForXtabTable(dashboardApi) {
		if (typeof (this._isNewCFForXtabTableEnabled) === 'boolean') {
			return this._isNewCFForXtabTableEnabled;
		}
		this._isNewCFForXtabTableEnabled = !(dashboardApi.getGlassCoreSvc('.FeatureChecker').checkValue('dashboard', 'xtabcondFormat', 'disabled'));
		return this._isNewCFForXtabTableEnabled;
	}
}

PropertiesPanel.propTypes = {
	dashboardApi: PropTypes.object.isRequired,
	widgetId: PropTypes.string.isRequired,
	widgetType: PropTypes.string.isRequired,
	isDisplayed: PropTypes.boolean
};

export default PropertiesPanel;


// WEBPACK FOOTER //
// ./src/js/components/properties/PropertiesPanel.js